import {
	FunctionComponent,
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { SettingsPage } from "../SettingsPage";
import {
	IntegrationCredentialsOverviewModel,
	IntegrationCredentialsOverviewViewModel,
	list,
	remove
} from "client/Api/IntegrationCredentials/integrationCredentials";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { localize } from "client_legacy/ndaw/culture";
import {
	AlertBar,
	computeContextMenuTitle,
	ContextMenu,
	FloatingActionButton,
	Pagination,
	SearchInputForm,
	SearchPlaceholder,
	TableView,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog,
	useContextMenu
} from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { contains } from "client/Data/searching";
import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import styles from "./OverviewPage.module.less";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter,
	route
} from "client/Navigation";
import { EditIntegrationCredentialsPageParams } from "./EditIntegrationCredentialsPage";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type OverviewPageProps = {
	onNavigate: NavigationCallback;
};

export const OverviewPage = (props: OverviewPageProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isAlertDismissed, setIsAlertDismissed] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const [selectedItems, setSelectedItems] = useState<readonly GridItem[]>([]);

	const [allItems, setAllItems] = useState<readonly GridItem[]>([]);

	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);

	const [sorting, setSorting] = useState<DataSourceSortableState<GridItem>>({
		direction: DataSourceSortDirection.Ascending,
		propertyName: "username"
	});

	const tableRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const alertManager: AlertManager = useAlerts();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();

	const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);

	useActiveSettingsMenuItem(SettingsPageKind.IntegrationCredentials);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal?: AbortSignal): Promise<void> {
				try {
					setIsBusy(true);
					const viewModel: IntegrationCredentialsOverviewViewModel = await list(signal)
						.catch(handlePageDataLoadingError);

					setAllItems(
						viewModel.credentials
							.map(convertToGridItem)
					);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const { sortingFor } = useSortHandler(
		sorting,
		setSorting,
		true
	);

	const {
		currentItems,
		pagination
	} = useDataSource(
		allItems,
		{
			searchTerm,
			pagination: {
				currentPageNumber,
				amountPerPage: 50
			},
			sorting
		}
		);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const newHandler = useCallback(
		(): void => {
			onNavigate(integrationCredentialsRouting.create);
		},
		[onNavigate]
	);

	const openHandler = useCallback(
		(id: number): void => {
			onNavigate(integrationCredentialsRouting.edit({credentialId: `${id}`}));
		},
		[onNavigate]
	);

	const deleteHandler = useCallback(
		async (items: readonly GridItem[]): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				title: localize("_AreYouSureYouWantToDeleteTheseCredentials"),
				children: items.length === 1
					? localize("_ThisItemWillBeDeleted")
					: localize("_TheseItemsWillBeDeleted"),
				confirmText: localize("_Delete"),
				trackingId: "delete-integration-credentials-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				const ids: readonly number[] = items.map((x) => x.id);
				await remove(ids, globalCancellation);

				setAllItems(
					(items) => items.filter(
						x => !ids.includes(x.id)
					)
				);

			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_ErrorDeletingCredentialsSets"));
			}

			setIsBusy(false);
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog
		]
	);

	const [selectionActions, contextualActions] = useMemo(
		(): [selectionlessActions: readonly ItemAction[], selectionActions: readonly ItemAction[]] => {
			const handlers: ContextualActionsHandlers = {
				edit: openHandler,
				delete: deleteHandler
			};

			return [
				getSelectionActions({
					deselectHandler: () => setSelectedItems([])
				}),
				listContextualActions(handlers)
			];
		},
		[
			deleteHandler,
			openHandler
		]
	);

	const actionContext = useMemo(
		(): ItemActionContext => {
			return {
				selectedItems,
				isMobile
			};
		},
		[isMobile, selectedItems]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<ItemActionContext> => {
			return getMainAction(newHandler);
		},
		[newHandler]
	);

	const hasSelection: boolean = actionContext.selectedItems
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ItemAction[] => {
			return buildActionsForTopBar<ItemActionContext, ItemAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				searchTerm,
				sorting
			);

			setCurrentPageNumber(0);
		},
		[searchTerm, sorting]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				searchTerm,
				sorting,
				currentPageNumber
			);

			tableRef.current
				?.scrollTo(0, 0);
		},
		[currentPageNumber, searchTerm, sorting]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={actionContext}
				title={localize("_IntegrationCredentials")}
				hasSelection={hasSelection}
			>
				{!isAlertDismissed && (
					<div className={styles.alertBarContainer}>
						<AlertBar
							kind="info"
							onDismiss={() => setIsAlertDismissed(true)}
							isInline
							shouldBeHiddenOnMobile
						>
							{localize("_HereYouCanSetTheUsernamesAndPasswordForApplicationsAndFileServersWhichAreIntegratedInTheWorkspace")}
						</AlertBar>
					</div>
				)}
				<SettingsPage.Section
					height="stretch"
				>
					<SettingsPage.SearchSection>
						<SearchInputForm
							onChange={setSearchTerm}
							icon="filter"
							height="large"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[
										localize("_Username"),
										localize("_UsedFor")
									]}
								/>
							)}
						/>
					</SettingsPage.SearchSection>

					<SettingsPage.Section height="stretch">
						<TableView
							items={currentItems}
							itemKey="id"
							columns={[
								{
									title: localize("_Username"),
									sortable: sortingFor("username"),
									trackingId: "username"
								},
								{
									title: localize("_UsedFor"),
									trackingId: "linkNames"
								}
							]}
							multiSelectable={multiSelection}
							trackingId="integration-credentials-table"
							ref={tableRef}
							height="stretch"
							noResultConfig={{
								icon: "gear",
								title: localize("_NoIntegrationCredentialsYet"),
								message: localize("_ThereAreNoIntegrationCredentialsYet")
							}}
						>
							{(x): ReactElement => (
								<TableView.Row
									trackingId={x.id.toString()}
									selectable={selectionFor(x)}
									onExecute={() => openHandler(x.id)}
									onContextMenu={contextMenuHandler}
								>
									<TableView.Cell shouldExecuteOnClick>
										{x.username}
									</TableView.Cell>
									<TableView.Cell>{x.linkNames}</TableView.Cell>
								</TableView.Row>
							)}
						</TableView>

						{contextMenuProps && (
							<ContextMenu
								{...contextMenuProps}
								actions={contextualActions}
								context={actionContext}
								trackingId="integration-credentials-context-menu"
								title={computeContextMenuTitle(
									actionContext.selectedItems,
									(x) => x.username
								)}
							/>
						)}

						<Pagination
							{...pagination}
							onPageNumberChanged={setCurrentPageNumber}
							trackingId="audit-log-pagination"
						/>
					</SettingsPage.Section>
				</SettingsPage.Section>

				<AiAssistantFabSpacing />

				{mainAction.canExecute(actionContext) && (
					<>
						<div className="preserve-space-for-mobile-plus-button"/>
						<FloatingActionButton
							onClick={() => mainAction.execute(actionContext)}
							icon="plus"
							trackingId="new-button"
						/>
					</>
				)}
			</SettingsPage>
			{confirmationDialog}
		</>
	);
};
OverviewPage.displayName = "OverviewPage";

export const OverviewPageForRouter: FunctionComponent = connectToRouter(
	OverviewPage,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

type GridItem = IntegrationCredentialsOverviewModel & { linkNames: string; };

function convertToGridItem(item: IntegrationCredentialsOverviewModel): GridItem {
	const {
		links: {
			environmentApps,
			exchange,
			fileServers,
			integrationFlowAuthentications
		}
	} = item;

	const linkNames: string[] = environmentApps
		.map((link) => link.name)
		.concat(
			integrationFlowAuthentications
				.map((x) => x.name)
		);

	if (exchange.isAvailable) {
		linkNames.push(localize("_Exchange"));
	}

	if (fileServers.isAvailable) {
		linkNames.push(localize("_FileServers"));
	}

	return {
		...item,
		linkNames: linkNames.sort().join(", ")
	};
}

const useDataSource = createLocalDataSource<GridItem>()
	.setConfiguration({
		hasPagination: true,
		search: {
			doesMatchItem: (row, searchTerm): boolean => {
				const doesUsernameMatch: boolean = contains(row.username, searchTerm);
				const doesLinkMatch: boolean = contains(row.linkNames, searchTerm);
				return doesUsernameMatch || doesLinkMatch;
			}
		}
	})
	.getHook();

type ItemActionContext = {
	selectedItems: readonly GridItem[];
	isMobile: boolean;
};

type ItemAction = ToolbarAction<ItemActionContext>;

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(handlers: SelectionActionsHandlers): readonly ItemAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selectedItems),
			icon: "circle-xmark",
			canExecute: (c) => c.selectedItems.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedItems.length > 0
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<ItemActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "new",
		icon: "plus",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newHandler(),
		isPrimary: true
	};
}

type ContextualActionsHandlers = {
	edit: (id: number) => void;
	delete: (items: readonly GridItem[]) => void;
};

function listContextualActions(handlers: ContextualActionsHandlers): readonly ItemAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			icon: "pen",
			title: localize("_Edit"),
			canExecute: (c) => c.selectedItems.length === 1,
			execute: (c) => handlers.edit(c.selectedItems[0].id)
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			icon: "trash-can",
			title: localize("_Delete"),
			canExecute: (c) => c.selectedItems.length > 0,
			execute: (c) => handlers.delete(c.selectedItems)
		}
	];
}


export const integrationCredentialsRouting = {
	overview: "/IntegrationCredentials",
	create: "/IntegrationCredentials/New",
	edit: route<EditIntegrationCredentialsPageParams>`/IntegrationCredentials/Edit/${"credentialId"}`
};